<template>
  <div>
    <h1 class="text-h4 mb-6">{{ $t('agent.title') }}</h1>

    <v-row>
      <v-col v-for="(agent, index) in agents" :key="index" cols="12" sm="6" md="4">
        <v-card class="agent-card" height="100%" @click="navigateToAgentDetail(agent.id)" style="cursor: pointer">
          <v-card-title class="text-h6 d-flex align-center">
            <v-icon class="mr-2" :color="getIconColor(agent.name)">{{ getAgentIcon(agent.name) }}</v-icon>
            {{ agent.name }}
            <v-chip
              size="small"
              class="ml-2"
              :color="getStatusColor(agent.name)"
              :text-color="getStatusTextColor(agent.name)">
              {{ getAgentStatus(agent.name) }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <div class="mb-2">
              <strong>{{ $t('agent.description') }}:</strong> {{ agent.description || $t('agent.noDescription') }}
            </div>
            <div class="mb-2">
              <strong>{{ $t('agent.model') }}:</strong> {{ agent.model }}
            </div>
            <div v-if="agent.data && agent.data.length > 0" class="mb-2">
              <strong>Data:</strong>
              <v-chip-group selected-class="text-info">
                <v-chip v-for="(dataItem, dataIndex) in agent.data" :key="dataIndex" size="small" color="info" variant="outlined" :value="dataIndex" select>
                  {{ dataItem }}
                </v-chip>
              </v-chip-group>
            </div>
            <div class="mb-2">
              <strong>{{ $t('agent.tools') }}:</strong>
              <v-chip-group selected-class="text-primary">
                <v-chip v-for="(tool, toolIndex) in agent.tools" :key="toolIndex" size="small" color="primary" variant="outlined" :value="toolIndex" select>
                  {{ tool }}
                </v-chip>
              </v-chip-group>
            </div>
            <div v-if="agent.linkedAgents && agent.linkedAgents.length > 0" class="mt-3">
              <strong>{{ $t('agent.linkedAgents') }}:</strong>
              <div class="d-flex flex-wrap gap-1 mt-1">
                <v-chip-group selected-class="text-secondary">
                  <v-chip v-for="(linkedAgent, linkIndex) in agent.linkedAgents" :key="linkIndex" size="small" color="secondary" variant="outlined" :value="linkIndex" select>
                    {{ linkedAgent }}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
          </v-card-text>


        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

// Function to get appropriate icon based on agent name
const getAgentIcon = (agentName: string) => {
  const name = agentName.toLowerCase();

  if (name.includes('nextgen')) return 'mdi-update';
  if (name.includes('lean')) return 'mdi-chart-timeline-variant';
  if (name.includes('api')) return 'mdi-api';
  if (name.includes('iobeya')) return 'mdi-application';
  if (name.includes('coordinator')) return 'mdi-account-supervisor';
  if (name.includes('sales')) return 'mdi-currency-usd';
  if (name.includes('discovery')) return 'mdi-file-document-outline';
  if (name.includes('briefing')) return 'mdi-file-document-outline';

  // Default icon
  return 'mdi-robot';
};

// Function to get color for icon based on agent name
const getIconColor = (agentName: string) => {
  const name = agentName.toLowerCase();

  if (name.includes('nextgen')) return 'info';
  if (name.includes('lean')) return 'success';
  if (name.includes('api')) return 'warning';
  if (name.includes('iobeya')) return 'primary';
  if (name.includes('coordinator')) return 'secondary';
  if (name.includes('sales')) return 'amber-darken-2';
  if (name.includes('discovery')) return 'indigo';
  if (name.includes('briefing')) return 'primary';

  // Default color
  return 'primary';
};

// Function to get agent status
const getAgentStatus = (agentName: string) => {
  const name = agentName.toLowerCase();

  if (name.includes('nextgen expert') || name.includes('lean expert') || name.includes('sales assistant') || name.includes('iobeya expert') || name.includes('discovery agent')) return 'Ready';
  if (name.includes('coordinator') || name.includes('api')) return 'Not Ready';

  // Default status
  return 'Not Ready';
};

// Function to get status color
const getStatusColor = (agentName: string) => {
  const status = getAgentStatus(agentName);

  if (status === 'Ready') return 'success';
  if (status === 'Not Ready') return 'error';

  return 'grey';
};

// Function to get status text color
const getStatusTextColor = (agentName: string) => {
  const status = getAgentStatus(agentName);

  if (status === 'Ready') return 'white';
  if (status === 'Not Ready') return 'white';

  return 'white';
};

import { useRouter } from 'vue-router';

const router = useRouter();

// Function to navigate to agent detail view
const navigateToAgentDetail = (id: string) => {
  router.push({ name: 'agentDetail', params: { id } });
};

const agents = ref([
  {
    id: '7', // Changed to match the correct ID for Discovery Agent
    name: 'Discovery Agent',
    description: 'Generates structured CRM reports from discovery call notes and transcriptions',
    model: 'GPT 4 Turbo',
    data: [],
    tools: ['Think'],
    linkedAgents: []
  },
  {
    id: '2',
    name: 'Lean Expert',
    description: 'Expert in lean management principles, methodologies, and best practices',
    model: 'GPT 4o',
    data: [],
    tools: ['Web Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '3',
    name: 'NextGen API Assistant',
    description: 'API specialist that translates business requirements into iObeya API operations',
    model: 'Claude 3.5 Sonnet',
    data: ['API model'],
    tools: ['Think', 'iObeya API'],
    linkedAgents: []
  },
  {
    id: '4',
    name: 'iObeya Expert',
    description: 'Expert in iObeya platform documentation, features and capabilities',
    model: 'Claude 3.5 Sonnet',
    data: ['iObeya Glossary', 'Resource Center'],
    tools: ['Resource Center Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '5',
    name: 'Coordinator',
    description: 'Orchestrator that routes user requests to the appropriate specialized agents',
    model: 'Claude 3.5 Sonnet',
    data: [],
    tools: ['Think'],
    linkedAgents: ['NextGen Expert', 'Lean Expert', 'NextGen API Assistant', 'iObeya Expert']
  },
  {
    id: '6',
    name: 'Sales Assistant',
    description: 'Sales specialist that provides support for Salesforce and sales-related queries',
    model: 'GPT 4o',
    data: [],
    tools: ['Salesforce', 'Web Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '1',
    name: 'NextGen Expert',
    description: 'Expert agent specialized in iObeya NextGen platform features and capabilities',
    model: 'Claude 3.5 Sonnet',
    data: ['iObeya Glossary'],
    tools: ['Think'],
    linkedAgents: []
  },
  {
    id: '8',
    name: 'Briefing Assistant',
    description: 'Briefing assistant agent responsible for analyzing incoming leads or user requests. Then create a briefing based on the lead information.',
    model: 'GPT 4o',
    data: [],
    tools: ['Hubspot Contact'],
    linkedAgents: []
  }
]);

</script>

<style scoped>
.agent-card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.agent-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}
</style>
